// ── Pure Task[] tree editing helpers ─────────────────────────────────────────
//
// `wf.tasks` is the single source of truth for workflow structure (see
// WorkflowEditor.tsx). These helpers return new trees — never mutate their
// input — so callers can persist the result directly via
// `updateTasks(workflowId, newTasks)`.
import { START_ID, END_ID, WEBHOOK_ID } from '../graph'
import { isBranchTask } from '../types'
import type { StepName, StepTask, BranchTask, Task } from '../types'

const SYNTHETIC_IDS = new Set<string>([START_ID, END_ID, WEBHOOK_ID])

function uid(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 9)}`
}

const STEP_DEFAULT_NAME: Record<StepName, string> = {
  MATCHLIST: 'Matchlist Check',
  KYC: 'KYC Check',
  IDV: 'IDV Check',
  AML: 'AML Check',
  KYB: 'KYB Check',
  FRAUD: 'Fraud & Device Risk Check',
  DECISION: 'Decision',
  TOGGLEMONITORING: 'Toggle Monitoring',
}

/** Appends a new StepTask to the end of the top-level chain. */
export function appendStep(tasks: Task[], stepName: StepName): Task[] {
  const newTask: StepTask = {
    id: uid(`step_${stepName.toLowerCase()}`),
    name: STEP_DEFAULT_NAME[stepName],
    configuration: { type: 'STEP', name: stepName, parameters: {} },
  }
  return [...tasks, newTask]
}

/** Appends a new BranchTask (with empty Pass/Else branches) to the end of the top-level chain. */
export function appendBranch(tasks: Task[]): Task[] {
  const newTask: BranchTask = {
    id: uid('branch'),
    name: 'Branch',
    configuration: {
      type: 'BRANCH',
      branches: [
        { name: 'Pass', tasks: [] },
        { name: 'Else', tasks: [] },
      ],
    },
  }
  return [...tasks, newTask]
}

/** Removes the task with the given id, recursing into branch subtrees. Synthetic (START/END/WEBHOOK) ids are ignored. */
export function removeTask(tasks: Task[], id: string): Task[] {
  if (SYNTHETIC_IDS.has(id)) return tasks

  return tasks
    .filter((t) => t.id !== id)
    .map((t) => {
      if (!isBranchTask(t)) return t
      return {
        ...t,
        configuration: {
          ...t.configuration,
          branches: t.configuration.branches.map((b) => ({
            ...b,
            tasks: removeTask(b.tasks, id),
          })),
        },
      }
    })
}

/** Finds the task with the given id anywhere in the tree (including nested inside branches). */
export function findTaskById(tasks: Task[], id: string): Task | undefined {
  for (const t of tasks) {
    if (t.id === id) return t
    if (isBranchTask(t)) {
      for (const b of t.configuration.branches) {
        const found = findTaskById(b.tasks, id)
        if (found) return found
      }
    }
  }
  return undefined
}

/** Replaces the task matching `id` with `updater(task)`, recursing into branch subtrees. */
export function updateTaskById(tasks: Task[], id: string, updater: (t: Task) => Task): Task[] {
  return tasks.map((t) => {
    if (t.id === id) return updater(t)
    if (!isBranchTask(t)) return t
    return {
      ...t,
      configuration: {
        ...t.configuration,
        branches: t.configuration.branches.map((b) => ({
          ...b,
          tasks: updateTaskById(b.tasks, id, updater),
        })),
      },
    }
  })
}
